import {DragEndEvent} from "@dnd-kit/core";
import {CoreHeader, flexRender, HeaderGroup, Row, RowModel} from "@tanstack/react-table";
import {default as classNames} from "classnames";
import {ReactNode} from "react";
import {FaCaretDown, FaCaretUp, FaSort} from "react-icons/fa6";

import {Button} from "@/components/button/Button.tsx";
import {SelectOption} from "@/components/select/Select.types.ts";
import {TableLoadingState} from "@/components/table/subComponents/TableLoadingState/TableLoadingState.tsx";
import {TableVariant} from "@/components/table/Table.types.ts";

import {DndContextWrapper} from "../DndContextWrapper/DndContextWrapper";
import {DraggableRow} from "../DraggableRow/DraggableRow";
import {LimitAndOffset, Paginator} from "../Paginator/Paginator.tsx";
import {SortableContextWrapper} from "../SortableContextWrapper/SortableContextWrapper";
import {StaticRow} from "../StaticRow/StaticRow";

import "./TableMarkup.css";
import {getPinnedCellProps} from "./TableMarkup.utils.ts";

import styles from "./TableMarkup.module.scss";

interface TableMarkupProps<TData> {
  actionBar?: ReactNode;
  headerGroups: HeaderGroup<TData>[];
  rowModel: RowModel<TData>;
  className?: string;
  headerClassName?: string;
  subrowRenderer?: (row: TData) => ReactNode;
  expandableSubRows?: boolean;
  fixedWidth?: boolean;
  minWidth?: string;
  noDataComponent?: ReactNode;
  tableRowOnClick?: (row: Row<TData>) => void;
  tableRowClassName?: string | undefined;
  dataTestId?: string;
  subRowEndSpacer?: boolean;
  isDragAndDrop?: boolean;
  onDragEnd?: (event: DragEndEvent) => void;
  isLoading?: boolean;
  fillContainerHeight?: boolean;
  variant?: TableVariant;
  placeholder?: boolean;
  numberOfColumns?: number;
  pagination?: LimitAndOffset;
  totalRows?: number;
  updatePagination?: (laf: LimitAndOffset) => void;
  paginationOptions?: SelectOption<number>[];
  hidePaginationOptions?: boolean;
  paginationData?: unknown[];
}

export const TableMarkup = <TData,>({
  actionBar,
  headerGroups,
  rowModel,
  className,
  headerClassName,
  subrowRenderer,
  noDataComponent,
  tableRowOnClick,
  expandableSubRows = false,
  fixedWidth = false,
  minWidth,
  tableRowClassName,
  subRowEndSpacer,
  dataTestId,
  isDragAndDrop = false,
  onDragEnd,
  isLoading,
  fillContainerHeight = false,
  variant = "default",
  placeholder = false,
  numberOfColumns,
  pagination,
  totalRows,
  updatePagination,
  paginationOptions,
  hidePaginationOptions,
  paginationData,
}: TableMarkupProps<TData>) => {
  const showPaginator = pagination && totalRows !== undefined && updatePagination && totalRows >= 5;
  // React 18 has no typing for `inert`; spreading a variable keeps it out of JSX excess-property checks
  const placeholderAttrs = placeholder ? {inert: ""} : {};

  return (
    <>
      <div
        className={classNames(styles.tableOuterWrapper, styles[variant], {
          [styles.fillOuterWrapper]: fillContainerHeight,
          [styles.placeholder]: placeholder,
        })}
        {...placeholderAttrs}
      >
        {actionBar && <div className={styles.actionBarScroll}>{actionBar}</div>}
        <div
          className={classNames(styles.tableWrapperScroll, {
            [styles.hasActionBar]: !!actionBar,
            [styles.hasPaginator]: showPaginator,
            [styles.fillContainerHeight]: fillContainerHeight,
          })}
        >
          <div
            className={classNames(styles.tableWrapper, {"shadow-sm": variant === "default"})}
            data-testid={dataTestId}
          >
            <div></div>
            <DndContextWrapper isDragAndDrop={isDragAndDrop} onDragEnd={(event: DragEndEvent) => onDragEnd?.(event)}>
              <table
                className={classNames("table", className, {fixedWidth: fixedWidth}, {[styles.tableFixed]: fixedWidth})}
                style={{minWidth: minWidth}}
              >
                {fixedWidth && headerGroups[0] && (
                  <colgroup>
                    {headerGroups[0].headers.map((header: CoreHeader<TData, unknown>) => (
                      <col key={header.id} className={header.column.columnDef.meta?.headerClassName} />
                    ))}
                  </colgroup>
                )}
                <thead className={classNames(headerClassName)}>
                  {headerGroups.map((headerGroup) => (
                    <tr
                      key={headerGroup.id}
                      className={classNames(styles.tableHeaderRow, {[styles.tableHeaderRowNoActionBar]: !actionBar})}
                    >
                      {headerGroup.headers.map((header: CoreHeader<TData, unknown>) => {
                        const canSort = header.column.getCanSort();
                        const pinnedProps = getPinnedCellProps(header.column);
                        const contentClassName = classNames(
                          header.column.columnDef.meta?.columnClassName,
                          canSort || header.column.columnDef.meta?.allowPointerEvents ? "" : "pe-none",
                          {
                            "d-flex align-items-center": header.column.columnDef.enableSorting,
                            [`align-content-${header.column.columnDef.meta?.headerTextAlignment || "start"}`]:
                              header.column.columnDef.enableSorting,
                          }
                        );
                        const content = (
                          <>
                            {flexRender(header.column.columnDef.header, header.getContext())}
                            {
                              {
                                asc: <FaCaretUp className="ms-1 align-top" />,
                                desc: <FaCaretDown className="ms-1 align-bottom" />,
                                false: header.column.columnDef.enableSorting ? (
                                  <FaSort className="ms-1 pulse-color-gray-500" />
                                ) : null, // Keeps the column widths the same
                              }[header.column.getIsSorted() as string]
                            }
                          </>
                        );
                        return (
                          <th
                            scope="col"
                            className={classNames(
                              header.column.columnDef.meta?.headerClassName,
                              "align-content-center pulseTextTableHeader",
                              canSort ? "p-0" : "p-2",
                              pinnedProps.className,
                              {
                                [styles.tableHeaderRowNoActionBar]: !actionBar,
                              }
                            )}
                            style={pinnedProps.style}
                            key={header.id}
                          >
                            {header.isPlaceholder ? null : canSort ? (
                              <Button
                                variant="tableSortHeader"
                                className={classNames(
                                  contentClassName,
                                  "p-2 w-100 h-100 cursor-pointer",
                                  `justify-content-${header.column.columnDef.meta?.headerTextAlignment || "start"}`
                                )}
                                onClick={header.column.getToggleSortingHandler()}
                              >
                                {content}
                              </Button>
                            ) : (
                              <div className={contentClassName}>{content}</div>
                            )}
                          </th>
                        );
                      })}
                    </tr>
                  ))}
                </thead>
                <tbody>
                  <SortableContextWrapper isDragAndDrop={isDragAndDrop} rows={rowModel.rows}>
                    {rowModel.rows.map((row: Row<TData>) => {
                      let addEndSpacer = false;
                      if (subRowEndSpacer && expandableSubRows && row.depth > 0) {
                        const parentRow = row.getParentRow();
                        if (parentRow) {
                          const subRows = parentRow.subRows;
                          if (subRows[subRows.length - 1].id === row.id) {
                            addEndSpacer = true;
                          }
                        }
                      }
                      if (subRowEndSpacer && expandableSubRows && subrowRenderer && row.getIsExpanded()) {
                        addEndSpacer = true;
                      }
                      if (isDragAndDrop) {
                        return (
                          <DraggableRow
                            row={row}
                            key={row.id}
                            subrowRenderer={subrowRenderer}
                            expandableSubRows={expandableSubRows}
                            addEndSpacer={addEndSpacer}
                            tableRowOnClick={tableRowOnClick}
                            tableRowClassName={tableRowClassName}
                          />
                        );
                      }
                      return (
                        <StaticRow
                          key={row.id}
                          row={row}
                          subrowRenderer={subrowRenderer}
                          expandableSubRows={expandableSubRows}
                          addEndSpacer={addEndSpacer}
                          tableRowOnClick={tableRowOnClick}
                          tableRowClassName={tableRowClassName}
                        />
                      );
                    })}
                    {rowModel.rows.length < 1 && !isLoading && noDataComponent}
                    {rowModel.rows.length < 1 && isLoading && <TableLoadingState colSpan={numberOfColumns} />}
                  </SortableContextWrapper>
                </tbody>
              </table>
            </DndContextWrapper>
          </div>
        </div>
        {showPaginator && (
          <Paginator
            className={styles.paginatorFooter}
            offset={pagination.offset}
            limit={pagination.limit}
            total={totalRows}
            updatePagination={updatePagination}
            options={paginationOptions}
            hideOptions={hidePaginationOptions}
            data={paginationData}
          />
        )}
      </div>
    </>
  );
};
